/**
 * Activity on one machine: the Activity page's timeline (`GET /activity/v1?deviceId=`) narrowed to it, today, this
 * week or this month. The platform answers from what the machine uploaded, so it reads the same while the machine is
 * away; asked once a minute while open (the answer's ETag keeps an unchanged minute to a 304), never while hidden.
 */

import { activityWindowStart, type ActivityResponse } from '@aioe/shared';
import { Clock } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useMemo, useState } from 'react';

import { routes } from '../../api/client.ts';
import { Timeline } from '../../components/activityTimeline.tsx';
import { ArtEmpty, Button, ErrorNote, ListBlock, ListGroup } from '../../components/ui.tsx';
import { useLoad, useNow } from '../../hooks.ts';
import type { Workbench } from '../useWorkbench.ts';
import { RemoteView } from './RemoteView.tsx';

type WindowKey = 'today' | 'week' | 'month';
const WINDOWS: Record<WindowKey, { label: string; days: number; spoken: string }> = {
  today: { label: 'Today', days: 1, spoken: 'today' },
  week: { label: '7 days', days: 7, spoken: 'in the last 7 days' },
  month: { label: '30 days', days: 30, spoken: 'in the last 30 days' },
};

export function ActivityView({ w }: { w: Workbench }) {
  const now = useNow();
  const [windowKey, setWindowKey] = useState<WindowKey>('today');
  const from = activityWindowStart(WINDOWS[windowKey].days, now);
  const query = useMemo(() => ({ from, deviceId: w.deviceId }), [from, w.deviceId]);
  const activity = useLoad(() => routes.activity(query), [query], 60_000, { whenVisible: true });
  const [older, setOlder] = useState<{ key: string; pages: ActivityResponse[]; loading?: boolean; error?: string }>({ key: '', pages: [] });
  const key = JSON.stringify(query);
  const pages = older.key === key ? older.pages : [];
  const a = activity.data;
  const next = (pages.at(-1) ?? a)?.next;
  const items = [...(a?.items ?? []), ...pages.flatMap((p) => p.items)];
  const showOlder = async () => {
    if (!next) return;
    setOlder({ key, pages, loading: true });
    try {
      setOlder({ key, pages: [...pages, await routes.activity({ ...query, before: next })] });
    } catch (e) {
      setOlder({ key, pages, error: (e as Error).message });
    }
  };
  return (
    <RemoteView
      narrow
      actions={
        <Segment aria-label="When" size="sm" selectedKey={windowKey} onSelectionChange={(k) => setWindowKey(String(k) as WindowKey)} className="w-fit">
          {(Object.keys(WINDOWS) as WindowKey[]).map((k) => (
            <Segment.Item key={k} id={k}>
              {WINDOWS[k].label}
            </Segment.Item>
          ))}
        </Segment>
      }
    >
      <ErrorNote error={activity.error} />
      <ListGroup
        title="Timeline"
        count={a?.totals.runs || undefined}
        description={`What ran here ${WINDOWS[windowKey].spoken}, newest first.`}
        empty={
          a && !items.length ? (
            <ArtEmpty
              compact
              tone="blue"
              icon={<Clock />}
              title="Nothing happened here in this window"
              action={windowKey !== 'month' ? <Button size="sm" variant="secondary" onPress={() => setWindowKey(windowKey === 'today' ? 'week' : 'month')}>Look further back</Button> : undefined}
            >
              Runs on this machine show here as it uploads them, with approvals, errors and finished tasks between them.
            </ArtEmpty>
          ) : undefined
        }
      >
        {!a && activity.loading ? <ListBlock className="text-sm text-muted">Reading what happened…</ListBlock> : null}
        {items.length ? (
          <ListBlock flush>
            <Timeline items={items} now={now} showProject showMachine={false} />
          </ListBlock>
        ) : null}
        {next || older.error ? (
          <ListBlock className="flex items-center gap-3">
            {next ? (
              <Button size="sm" variant="ghost" isPending={older.loading} onPress={() => void showOlder()}>
                Show older
              </Button>
            ) : null}
            <ErrorNote error={older.error} />
          </ListBlock>
        ) : null}
      </ListGroup>
    </RemoteView>
  );
}
